import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Loader2, XCircle, Car } from 'lucide-react';
import { toast } from 'sonner';

export default function MigrateSuccess() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // 'checking' | 'success' | 'error'
  const [upgradeDetails, setUpgradeDetails] = useState(null);

  useEffect(() => {
    const changeId = searchParams.get('change_id');
    const sessionId = searchParams.get('session_id');
    
    if (!changeId || !sessionId) {
      toast.error('Parâmetros inválidos');
      navigate('/client/plan-details');
      return;
    }
    
    confirmUpgrade(changeId, sessionId);
  }, [searchParams]);

  const confirmUpgrade = async (changeId, sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Confirmar upgrade no backend (aplica a troca após pagamento)
      const res = await axios.post(
        `${API}/client/vehicle/upgrade/confirm?change_id=${changeId}&session_id=${sessionId}`,
        {},
        { headers }
      );
      
      setUpgradeDetails(res.data);
      setStatus('success');
      
    } catch (error) {
      console.error('Erro ao confirmar upgrade:', error);
      
      // Se pagamento ainda não foi confirmado, tentar polling
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('não confirmado')) {
        let attempts = 0;
        const maxAttempts = 5;
        
        const poll = async () => {
          try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            const res = await axios.post(
              `${API}/client/vehicle/upgrade/confirm?change_id=${changeId}&session_id=${sessionId}`,
              {},
              { headers }
            );
            
            setUpgradeDetails(res.data);
            setStatus('success');
          } catch (err) {
            if (attempts < maxAttempts) {
              attempts++;
              setTimeout(poll, 2000);
            } else {
              setStatus('error');
            }
          }
        };
        
        setTimeout(poll, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="migrate-success-page">
      <Card className="w-full max-w-md p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Processando Upgrade...</h2>
            <p className="text-muted-foreground">Aguarde enquanto confirmamos seu pagamento e aplicamos a mudança</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Upgrade Concluído!</h2>
            <p className="text-muted-foreground mb-4">
              Seu veículo foi alterado com sucesso
            </p>
            
            {upgradeDetails?.new_vehicle && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Car className="w-5 h-5 text-primary" />
                  <span className="font-semibold">Novo Veículo</span>
                </div>
                <p className="text-lg font-medium">
                  {upgradeDetails.new_vehicle.brand} {upgradeDetails.new_vehicle.model}
                </p>
                <p className="text-sm text-muted-foreground">
                  Placa: {upgradeDetails.new_vehicle.plate}
                </p>
              </div>
            )}
            
            <Button 
              onClick={() => navigate('/client/plan-details')} 
              className="w-full" 
              data-testid="goto-plan-details-btn"
            >
              Ver Detalhes do Plano
            </Button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Erro no Processamento</h2>
            <p className="text-muted-foreground mb-6">
              Houve um problema ao processar seu upgrade. Por favor, entre em contato com o suporte.
            </p>
            <div className="space-y-2">
              <Button 
                onClick={() => navigate('/client/plan-details')} 
                className="w-full"
              >
                Voltar aos Detalhes do Plano
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.reload()} 
                className="w-full"
              >
                Tentar Novamente
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
